import { z } from 'zod';

import type { AuthorizationScope } from '../../libs/auth/rbac';
import { canonicaliseEmail } from '../../libs/consolidation/src/identity/email-resolver';
import { isValidGithubLogin, ME_PERSON } from './github-login';

// ---------------------------------------------------------------------------
// Unified person directory — the cross-provider identity model behind the
// issues "people" filter. A "person" spans GitHub and/or Linear, matched by an
// explicit map + email + name (see `libs/account/person-directory.ts`). Selection
// uses a heterogeneous, URL-safe **token**: `@me`, a GitHub login, or
// `linear:<userId>` for a Linear-only person (no GitHub account). Served by
// GET /api/account/people.
// ---------------------------------------------------------------------------

export const PersonProviderSchema = z.enum(['github', 'linear']);
export type PersonProvider = z.infer<typeof PersonProviderSchema>;

/** Prefix for a Linear-only person's token (`linear:<userId>`). */
export const LINEAR_TOKEN_PREFIX = 'linear:';

/** Explicit people scope used by the shared work-items providers. */
export type WorkItemsPeopleScope =
  | { readonly kind: 'me' }
  | { readonly kind: 'everyone' }
  | { readonly kind: 'selected'; readonly people: readonly string[] };

// A Linear person token: `linear:` + a bounded, syntactically-constrained id (Linear
// user ids are UUID-like). The length + charset bound stops a caller minting giant or
// arbitrary tokens that would balloon the board-snapshot scope key (one row per
// distinct people selection). `:` is allowed so a `<connectionId>:<userId>` id form
// still validates.
const LINEAR_PERSON_TOKEN = /^linear:[\w:-]{1,128}$/;

/**
 * True when `token` is a usable people-selection token: the `@me` sentinel, a
 * syntactically valid GitHub login, or a bounded `linear:<userId>` reference.
 * Validates syntax only (not membership) — like #2425's `isValidPersonToken`, but
 * also accepting Linear-only people.
 */
export function isPersonToken(token: string): boolean {
  return (
    token === ME_PERSON ||
    isValidGithubLogin(token) ||
    LINEAR_PERSON_TOKEN.test(token)
  );
}

/**
 * How confidently a Linear identity was matched to a GitHub one: an explicit
 * user mapping (highest), an email match, a name match (lowest), or no
 * cross-provider match at all.
 */
export const PersonConfidenceSchema = z.enum([
  'explicit',
  'email',
  'name',
  'single-source',
]);
export type PersonConfidence = z.infer<typeof PersonConfidenceSchema>;

/** A Linear user in one connected workspace. */
export const LinearReferenceSchema = z.object({
  connectionId: z.string().min(1),
  userId: z.string().min(1),
});
export type LinearReference = z.infer<typeof LinearReferenceSchema>;

/** One selectable person in the unified directory. */
export const DirectoryPersonSchema = z.object({
  /** Selection/URL token: `@me`, a GitHub login, or `linear:<userId>`. */
  token: z.string().min(1),
  displayName: z.string().min(1),
  providers: z.array(PersonProviderSchema).min(1),
  /** The person's GitHub login, or `null` for a Linear-only person. */
  githubLogin: z.string().nullable(),
  /** The person's Linear users, one per workspace they appear in (may be empty). */
  linearRefs: z.array(LinearReferenceSchema),
  /** True for the viewer's own person (resolves the `@me` token). */
  isMe: z.boolean(),
  confidence: PersonConfidenceSchema,
});
export type DirectoryPerson = z.infer<typeof DirectoryPersonSchema>;

/** A selectable Linear user, for the mapping picker (`<connectionId>:<userId>`). */
export const LinearUserOptionSchema = z.object({
  ref: z.string().min(1),
  label: z.string().min(1),
  workspace: z.string(),
});
export type LinearUserOption = z.infer<typeof LinearUserOptionSchema>;

// ---------------------------------------------------------------------------
// Cross-provider groups — a GitHub team mapped to a Linear team. Org-scoped (a
// GitHub team belongs to one org), GitHub-anchored: each group is a GitHub team
// with an optional mapped Linear team. Selection uses a `team:<slug>` token (the
// org-relative GitHub team slug; the org is the page scope).
// ---------------------------------------------------------------------------

/** Prefix for a cross-provider group token (`team:<slug>`). */
export const TEAM_TOKEN_PREFIX = 'team:';

/**
 * True when `token` selects a cross-provider group — a `team:<slug>` reference
 * (the org-relative GitHub team slug). Validates syntax only, not membership.
 */
export function isGroupToken(token: string): boolean {
  return (
    token.startsWith(TEAM_TOKEN_PREFIX) &&
    token.length > TEAM_TOKEN_PREFIX.length
  );
}

/** How confidently a GitHub team was matched to a Linear team. */
export const GroupConfidenceSchema = z.enum([
  'explicit',
  'name',
  'single-source',
]);
export type GroupConfidence = z.infer<typeof GroupConfidenceSchema>;

/** The Linear team a GitHub team maps to, in one connected workspace. */
export const LinearTeamReferenceSchema = z.object({
  connectionId: z.string().min(1),
  teamId: z.string().min(1),
});
export type LinearTeamReference = z.infer<typeof LinearTeamReferenceSchema>;

/** One selectable group: a GitHub team with an optional mapped Linear team. */
export const DirectoryTeamSchema = z.object({
  /** Selection token `team:<slug>` (the org-relative GitHub team slug). */
  token: z.string().min(1),
  displayName: z.string().min(1),
  /** The GitHub org (login) this team belongs to. */
  githubOrg: z.string().min(1),
  /** The org-relative GitHub team slug. */
  githubSlug: z.string().min(1),
  /** The mapped Linear team, or `null` when unmapped. */
  linearRef: LinearTeamReferenceSchema.nullable(),
  confidence: GroupConfidenceSchema,
});
export type DirectoryTeam = z.infer<typeof DirectoryTeamSchema>;

/** A selectable Linear team, for the mapping picker (`<connectionId>:<teamId>`). */
export const LinearTeamOptionSchema = z.object({
  ref: z.string().min(1),
  label: z.string().min(1),
  workspace: z.string(),
});
export type LinearTeamOption = z.infer<typeof LinearTeamOptionSchema>;

/**
 * A human-authored email alias group from the tenant directory. Provider facts
 * are intentionally absent: this is only the explicit override layer.
 */
export const IdentityOverrideSchema = z.object({
  canonicalEmail: z.string().min(1),
  aliasEmails: z.array(z.string().min(1)).min(1),
});
export type IdentityOverride = z.infer<typeof IdentityOverrideSchema>;

/** Response envelope for `GET /api/account/people`. */
export const PeopleResponseSchema = z.object({
  /** True when at least one provider (GitHub or Linear) is connected in scope. */
  connected: z.boolean(),
  people: z.array(DirectoryPersonSchema),
  /** Every connected Linear user, so the mapping panel can offer them as targets. */
  linearUsers: z.array(LinearUserOptionSchema),
  /** Cross-provider groups (GitHub teams + optional Linear team). Org scope only. */
  teams: z.array(DirectoryTeamSchema),
  /** Every connected Linear team, so the mapping panel can offer them as targets. */
  linearTeams: z.array(LinearTeamOptionSchema),
  /**
   * The Linear workspace connection id mapped to the in-scope org, or `null`
   * (personal scope, or an unmapped org). Lets the mapping picker show the org's
   * current workspace choice without a separate fetch.
   */
  linearWorkspace: z.string().min(1).nullable(),
  /** Human email aliases applied before automatic email/name matching. */
  identityOverrides: z.array(IdentityOverrideSchema).default([]),
  /** True when a provider's member/user page overflowed and was capped. */
  truncated: z.boolean(),
});
export type PeopleResponse = z.infer<typeof PeopleResponseSchema>;

// ---------------------------------------------------------------------------
// Row-scoped variant (#2851 `people.read`)
//
// Derived from `PeopleResponseSchema` by narrowing bounds, never by hand-writing
// a second envelope: a field added to the directory appears here too, still
// bounded. The envelope keeps its shape so every existing consumer still parses;
// what changes is which ROWS the server produced, which is the row-scope rule the
// policy states — not a client-side redaction of a full payload.
// ---------------------------------------------------------------------------

/**
 * At most the viewer's own person, and none of the organization-wide pickers.
 *
 * `linearWorkspace` and `truncated` are narrowed too: the first is an
 * organization's Linear CONNECTION id and the second reports whether the
 * organization directory overflowed. Both describe the org, not the viewer, so a
 * self-scoped answer must not carry either — the schema makes that a parse error
 * rather than a convention.
 */
export const SelfScopedPeopleResponseSchema = PeopleResponseSchema.extend({
  people: z.array(DirectoryPersonSchema).max(1),
  linearUsers: z.array(LinearUserOptionSchema).max(0),
  teams: z.array(DirectoryTeamSchema).max(0),
  linearTeams: z.array(LinearTeamOptionSchema).max(0),
  identityOverrides: z.array(IdentityOverrideSchema).max(0).default([]),
  linearWorkspace: z.null(),
  truncated: z.literal(false),
});
export type SelfScopedPeopleResponse = z.infer<
  typeof SelfScopedPeopleResponseSchema
>;

export type PeopleContractVariant = PeopleResponse | SelfScopedPeopleResponse;

/**
 * Project the directory onto the row scope the policy granted.
 *
 * Only `organization` yields the whole directory. Every other scope collapses to
 * the viewer's own row, which is fail-closed by construction — a scope this
 * function does not know about can never widen the result.
 *
 * `team` deliberately lands in the self branch: the unified directory carries no
 * per-person team membership, so a team-scoped grant can prove nothing beyond the
 * viewer's own row. It widens when team membership becomes part of the contract,
 * not before.
 */
export function selectPeopleContractVariant(
  response: Readonly<PeopleResponse>,
  scope: AuthorizationScope,
): PeopleContractVariant {
  if (scope === 'organization') return PeopleResponseSchema.parse(response);
  return SelfScopedPeopleResponseSchema.parse({
    ...response,
    people: response.people.filter((person) => person.isMe).slice(0, 1),
    linearUsers: [],
    teams: [],
    linearTeams: [],
    identityOverrides: [],
    linearWorkspace: null,
    truncated: false,
  });
}

/** Write one canonical email's human-managed aliases in an org workspace. */
export const IdentityOverrideRequestSchema = z
  .object({
    org: z.string().trim().min(1),
    canonicalEmail: z.string().trim().email(),
    aliasEmails: z.array(z.string().trim().email()).min(1).max(50),
  })
  .superRefine((value, context) => {
    const canonical = canonicaliseEmail(value.canonicalEmail);
    for (const [index, alias] of value.aliasEmails.entries()) {
      if (canonicaliseEmail(alias) === canonical) {
        context.addIssue({
          code: 'custom',
          message: 'An alias must differ from the canonical email',
          path: ['aliasEmails', index],
        });
      }
    }
  });
export type IdentityOverrideRequest = z.infer<
  typeof IdentityOverrideRequestSchema
>;

export const IdentityOverrideWriteSuccessSchema = z.object({
  ok: z.literal(true),
});
export type IdentityOverrideWriteSuccess = z.infer<
  typeof IdentityOverrideWriteSuccessSchema
>;

export const IdentityOverrideWriteErrorSchema = z.object({
  error: z.enum([
    'unauthenticated',
    'payment_required',
    'invalid_request',
    'forbidden',
    'conflict',
    'unavailable',
  ]),
});
export type IdentityOverrideWriteError = z.infer<
  typeof IdentityOverrideWriteErrorSchema
>;

// A GitHub org login the map is keyed by: trimmed, syntactically valid (so a
// malformed login can't be persisted as a mapping key).
const GithubOrgSchema = z.string().trim().refine(isValidGithubLogin);
// A Linear reference `"<connectionId>:<id>"` (or `null` to clear): non-empty on
// both sides of the first colon. The route additionally checks the connection is
// one of the caller's connected workspaces.
const LinearReferenceRequestSchema = z
  .string()
  .regex(/^[^:]+:.+$/)
  .nullable();

/**
 * Request body for `POST /api/account/github-linear-map` — within a GitHub org
 * (`githubOrg`, the Clerk org the map is stored on), map a GitHub login to a Linear
 * user (`"<connectionId>:<userId>"`), or clear it with a `null` ref.
 */
export const GithubLinearMapRequestSchema = z.object({
  githubOrg: GithubOrgSchema,
  githubLogin: z.string().min(1),
  linearRef: LinearReferenceRequestSchema,
});
export type GithubLinearMapRequest = z.infer<
  typeof GithubLinearMapRequestSchema
>;

/**
 * Request body for `POST /api/account/github-linear-team-map` — within a GitHub org
 * (`githubOrg`, the Clerk org the map is stored on), map a GitHub team slug to a
 * Linear team (`"<connectionId>:<teamId>"`), or clear it with a `null` ref.
 */
export const GithubLinearTeamMapRequestSchema = z.object({
  githubOrg: GithubOrgSchema,
  githubSlug: z.string().min(1),
  linearRef: LinearReferenceRequestSchema,
});
export type GithubLinearTeamMapRequest = z.infer<
  typeof GithubLinearTeamMapRequestSchema
>;

/**
 * Org-scoped mapping context passed from a route to the directory / issues /
 * work-items cores: the GitHub org login (to fetch its members/teams) plus the
 * Clerk org's public metadata (the org-shared people/team/workspace maps). The
 * route resolves it, membership-gated via `resolveOrgFromSlug`. A plain type (no
 * server deps) so test files can import the cores' input types without pulling in
 * the server-only credential chain.
 */
export interface OrgMappingScope {
  readonly githubLogin: string;
  readonly publicMetadata: OrganizationPublicMetadata;
}
